import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import classNames from "classnames";
import * as React from "react";

import styles from "./Tabs.module.css";

type BaseUIClassName<State> =
  string | ((state: State) => string | undefined) | undefined;

/**
 * Merges a house class with a consumer's. Base UI's `className` is either a
 * string or a function of the part's state, so both forms have to survive.
 */
function withHouseClass<State>(
  houseClass: string,
  className: BaseUIClassName<State>,
): BaseUIClassName<State> {
  return typeof className === "function"
    ? (state: State) => classNames(houseClass, className(state))
    : classNames(houseClass, className);
}

export interface TabsProps<Value extends string = string>
  extends BaseTabs.Root.Props {
  /** The value of the active tab. */
  value: Value;
}

/**
 * A row of tabs, one of them active, with an underline that slides to the
 * active tab's label. The tabs are 40px tall with 14px labels: a strip that
 * sits under a page's header, not a control inside a form.
 *
 * The caller owns the value. A tab whose `render` is a link navigates on its
 * own, and the caller reads the active tab back from the URL; a tab without
 * one reports a click through `onValueChange`.
 *
 * Panels are the caller's: a strip under a page header has a route outlet
 * for a panel, so there is no `TabPanel` part.
 *
 * @example
 * <Tabs value={tab} onValueChange={setTab}>
 *   <TabList aria-label="Order">
 *     <Tab value="items">Items</Tab>
 *     <Tab value="history">History</Tab>
 *     <TabSeparator />
 *     <Tab value="settings">Settings</Tab>
 *   </TabList>
 * </Tabs>
 */
export function Tabs<Value extends string = string>({
  className,
  ...props
}: TabsProps<Value>) {
  return (
    <BaseTabs.Root
      {...props}
      className={withHouseClass(styles.root, className)}
    />
  );
}

export interface TabListProps extends BaseTabs.List.Props {
  /** Names the strip for assistive tech, as "dataset tabs". */
  "aria-label"?: string;
}

/**
 * The strip holding the tabs, plus the underline that marks the active one.
 * Arrow keys move focus between tabs; Enter or Space opens the focused tab.
 */
export const TabList = React.forwardRef<HTMLDivElement, TabListProps>(
  function TabList({ className, children, ...props }, ref) {
    return (
      <BaseTabs.List
        {...props}
        ref={ref}
        className={withHouseClass(styles.list, className)}
      >
        {children}
        <BaseTabs.Indicator className={styles.indicator} />
      </BaseTabs.List>
    );
  },
);

export interface TabProps<Value extends string = string> extends Omit<
  BaseTabs.Tab.Props,
  "value"
> {
  /** What `Tabs` holds as its `value` when this tab is active. */
  value: Value;
}

/**
 * One tab. Its children are its content: an icon, the label, a status after
 * it. Pass `render` with an anchor or router `Link` and `nativeButton={false}`
 * to make the tab a link.
 */
export const Tab = React.forwardRef<HTMLButtonElement, TabProps>(function Tab(
  { className, ...props },
  ref,
) {
  return (
    <BaseTabs.Tab
      {...props}
      ref={ref}
      className={withHouseClass(styles.tab, className)}
    />
  );
}) as <Value extends string = string>(
  props: TabProps<Value> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactElement;

/** A short vertical rule between two groups of tabs. */
export function TabSeparator({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      {...props}
      className={classNames(styles.separator, className)}
    />
  );
}
